import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate, Share } from '@mui/icons-material'
import { Typography, MenuItem, ListItemIcon, Divider, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { AccessKeySelection } from 'domains/Showcases/Collections/AccessKeys/AccessKeyQRCode'
import { useCollectionCreateMutation, useCollectionDeleteMutation } from 'hooks/mutations/showcase'
import { collectionsOverviewRoute } from 'pages/NWPlatform/Collections'
import { collectionRoute } from 'pages/NWPlatform/Collections/Collection'
import { collectionQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { collectionId } = collectionRoute.useParams()
  const navigate = useNavigate({ from: collectionRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [shareDialogOpen, setShareDialogOpen] = useState(false)

  const collectionQuery = useQuery(collectionQueryKeys.detail({ collectionId }))
  const { data: collection } = collectionQuery

  const { mutate: createCollection, ...collectionCreateMutation } = useCollectionCreateMutation()
  const { mutate: deleteCollection } = useCollectionDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!collection) return

    setAnchorEl(undefined)

    deleteCollection(
      { collectionId: collection.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Showcase.Collection.nameDelete', { name: collection.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: collectionsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [collection, deleteCollection, enqueueSnackbar, navigate, t])

  const handleDuplicateCollection = useCallback(() => {
    if (!collection) return

    setAnchorEl(undefined)

    createCollection(
      { showcaseCollectionInsert: { ...collection, name: `${collection.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [collection, createCollection, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={collection?.name}
        entity={t('entity:showcase.collection', { count: 1 })}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)
            setTimeout(() => {
              setShareDialogOpen(true)
            }, 300)
          }}
        >
          <ListItemIcon>
            <Share fontSize="small" />
          </ListItemIcon>
          <Typography variant="text">{t('domain:Showcase.Collection.shareCollection')}</Typography>
        </MenuItem>

        <MenuItem onClick={handleDuplicateCollection}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {collectionCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={collectionId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Showcase.Collection.deleteCollection', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: 'max-content',
              maxWidth: 1120,
            },
          },
        }}
      >
        <AccessKeySelection />
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
